/**
 * Shared types for the carousel template system.
 *
 * A template is a set of three draw functions (cover / species / closing)
 * that render onto a 1080×1350 Canvas 2D context. Templates receive a
 * SlideStyle carrying the resolved font families and the watermark
 * entitlement — the render path is identical for every tier; premium
 * simply means `watermark: null` or an own-handle watermark text.
 */

export const SLIDE_W = 1080;
export const SLIDE_H = 1350;

export interface SlideStyle {
  fonts: {
    display: string; // resolved font-family for headlines/species names
    body: string;    // resolved font-family for labels/context text
  };
  /** null only when the user is entitled to watermark removal */
  watermark: { text: string } | null;
}

export interface CoverStat {
  icon: string;  // emoji, editable
  value: string;
}

export interface CoverSlideData {
  /** e.g. "FIELD DIARY · No. 57" — fully editable top label */
  kickerText: string;
  headlineTitle: string;
  headlineFontSize: number; // px, user-adjustable — default 140
  subtitle: string;         // e.g. "ALMATY · JUNE 2026"
  stats: CoverStat[];       // 0–4 bottom stat segments
  photo?: HTMLImageElement | null;
  /**
   * Per-element size multipliers (1 = the template's design size).
   * An empty string in the matching text field hides the element and the
   * layout reflows around it.
   */
  kickerScale?: number;
  subtitleScale?: number;
  statsScale?: number;
  /**
   * Overrides the baked swipe cue ("СВАЙП", "ЛИСТАЙ ДАЛЬШЕ", …) so it can be
   * localized. Empty/undefined keeps each template's own default wording.
   */
  swipeText?: string;
  /**
   * Vertical position of the cover photo within the slide crop: -1 shows
   * the top of the photo, 0 centers (default), 1 shows the bottom. Lets the
   * user keep the subject visible under templates with solid panels/bars.
   */
  photoOffsetY?: number;
  /** Photo zoom multiplier on top of the cover-fit scale; 1 = no extra zoom. */
  photoZoom?: number;
  /**
   * When there's no photo: "auto" (default) paints the template's own
   * background so the slide is never left blank/transparent; "transparent"
   * is an explicit opt-in to a see-through PNG; "light"/"dark"/"accent" pick
   * a curated flat-color background instead of the template's own tone
   * (still runs the same readability gradient + text-color flip as
   * "transparent" so text stays legible). Ignored once a photo is set
   * (the photo always covers the full slide).
   */
  backgroundMode?: "auto" | "transparent" | "light" | "dark" | "accent";
}

export interface SpeciesSlideData {
  /** Top-most, largest name — whichever language is active (see
   * lib/carousel/names.ts), English by default. */
  namePrimary: string | null;
  /** Middle, medium-size name — the scientific/Latin name when shown. */
  nameSecondary: string | null;
  /** Bottom, subdued name — English, shown only when a regional language
   * (Russian/Kazakh) occupies namePrimary instead; empty otherwise. */
  nameTertiary: string;
  photo?: HTMLImageElement | null;
  photoOffsetY?: number;
  photoZoom?: number;
}

export interface ClosingSlideData {
  heading: string;   // e.g. "Thanks for scrolling"
  subtext: string;   // e.g. "follow for more field diaries"
  handle: string;    // e.g. "@dala.explorer"
  photo?: HTMLImageElement | null;
  photoOffsetY?: number;
  photoZoom?: number;
}

/** Passed to draw*() by the live editor stage (never by export) to leave a
 * field's canvas text unpainted because a real HTML input is overlaid on
 * top of it instead — see `useTextOverlay` below. */
export interface DrawOpts {
  skipEditableText?: boolean;
}

export interface CarouselTemplate {
  id: string;
  name: string;
  premium: boolean;
  drawCover(ctx: CanvasRenderingContext2D, data: CoverSlideData, style: SlideStyle, opts?: DrawOpts): void;
  drawSpecies(ctx: CanvasRenderingContext2D, data: SpeciesSlideData, style: SlideStyle, opts?: DrawOpts): void;
  drawClosing(ctx: CanvasRenderingContext2D, data: ClosingSlideData, style: SlideStyle, opts?: DrawOpts): void;
  /** Per-slide-kind: true where the editor should overlay real HTML inputs
   * for this template's editable text (positioned via that template's own
   * exported layout helpers) instead of relying on canvas-rendered text —
   * click text on the slide, type directly. A kind can be omitted/false when
   * that draw function's layout hasn't gotten an overlay yet (falls back to
   * the old bottom-panel-only editing for that slide). Canvas still draws
   * the full text when exporting (opts is only ever passed live, never on
   * export). */
  useTextOverlay?: { cover?: boolean; species?: boolean; closing?: boolean };
}
